import React from "react";
import TextCard from "@/components/TextCard/TextCard";
import IconCardGrid from "@/components/IconCardGrid/IconCardGrid";
import ListItemsGrid from "@/components/ListItemsGrid/ListItemsGrid";
import QRCode from "@/components/QRCode/QRCode";
import CTABanner from "@/components/CTABanner/CTABanner";
import CardsGrid from "@/components/CardsGrid/CardsGrid";
import { ModalContent, ModalContentItem, TextCardContent } from "@/types";
import { ScrollArea } from "@/components/ui/scroll-area";
import Disclaimers from "@/components/Disclaimers/Disclaimers";
import { useGaEvent } from "@/hooks/useGaEvent";

type ModalLayoutContentProps = {
  content: ModalContent;
};

const renderContentItem = (item: ModalContentItem, index: number) => {
  switch (true) {
    case !!item.TextCard:
      return (
        <TextCard
          key={index}
          type={item.TextCard.type}
          alignment={item.TextCard.alignement}
          title={item.TextCard.title}
          subtitle={item.TextCard.subtitle}
          description={item.TextCard.description}
          gap={item.TextCard.gap}
        />
      );
    case !!item.Heading:
      return (
        <h2 key={index} className="mt-8 text-center font-regular text-3xl text-black">
          {item.Heading.label}
        </h2>
      );
    case !!item.IconCardGrid:
      return (
        <div key={index} className="flex justify-center">
          <IconCardGrid
            columns={item.IconCardGrid.columns}
            iconCards={item.IconCardGrid.items}
          />
        </div>
      );
    case !!item.ListItemsGrid:
      return (
        <div key={index} className="flex justify-center">
          <ListItemsGrid listItems={item.ListItemsGrid} />
        </div>
      );
    case !!item.CardsGrid:
      return (
        <div key={index} className="flex justify-center">
          <CardsGrid columns={item.CardsGrid.columns} Cards={item.CardsGrid.items} />
        </div>
      );
    case !!item.QRCode:
      return (
        <div key={index} className="flex justify-center">
          <QRCode size={item.QRCode.size} src={item.QRCode.src} text={item.QRCode.text} />
        </div>
      );
    case !!item.CTABanner:
      return (
        <div key={index} className="flex justify-center">
          <CTABanner
            title={item.CTABanner.title}
            subtitle={item.CTABanner.subtitle}
            bgColour={item.CTABanner.bgColour}
          />
        </div>
      );
    case !!item.Disclaimers:
      return (
        <Disclaimers
          title={item.Disclaimers.title}
          disclaimerItems={item.Disclaimers.disclaimerItems}
        />
      );
    default:
      return null;
  }
};

const getModalTitle = (content: ModalContent) => {
  const modalIntro = content.find((item) =>
    Object.values(item).some((obj: any) => obj?.type === "intro"),
  );
  return (
    modalIntro &&
    (Object.values(modalIntro).find(
      (obj: any) => obj?.type === "intro",
    ) as TextCardContent)
  );
};

const ModalLayoutContent: React.FC<ModalLayoutContentProps> = ({ content }) => {
  const modalTitle = getModalTitle(content);
  useGaEvent("screen_view", { screen_title: modalTitle?.title });
  return (
    <ScrollArea className="h-[920px] w-full">
      <div className="select-none p-4">
        {content.map((item, index) => renderContentItem(item, index))}
      </div>
    </ScrollArea>
  );
};

export default ModalLayoutContent;
